import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import api from "../../Utils/api";
import * as XLSX from "xlsx";

import {
  getCurrentDateTime,
  formatDate,
} from "../../Utils/commonfunctions";

import Spinner from "react-bootstrap/Spinner";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./GetMachineryProductionReport.css";

const GetMachineryProductionReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [summary, setSummary] = useState([]);
  const [details, setDetails] = useState([]);

  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const StartDateRef = useRef(null);

  // =========================================================
  // INITIAL DATE
  // =========================================================
  useEffect(() => {
    if (StartDateRef.current) {
      StartDateRef.current.focus();
    }

    setStartDate(getCurrentDateTime());
    setEndDate(getCurrentDateTime());
  }, []);

  // =========================================================
  // CLEAR
  // =========================================================
  const ClearData = () => {
    setSummary([]);
    setDetails([]);
    setShowTable(false);

    setStartDate(getCurrentDateTime());
    setEndDate(getCurrentDateTime());
  };

  // =========================================================
  // GET VALUE
  // Supports multiple possible API field names
  // =========================================================
  const getValue = (row, fields, defaultValue = 0) => {
    for (const field of fields) {
      if (
        row &&
        row[field] !== undefined &&
        row[field] !== null &&
        row[field] !== ""
      ) {
        return row[field];
      }
    }

    return defaultValue;
  };

  // =========================================================
  // NUMBER FORMAT
  // =========================================================
  const number = (value, decimal = 2) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "0.00";
    }

    const n = Number(value);

    if (isNaN(n)) {
      return value;
    }

    return n.toLocaleString("en-IN", {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  };

  // =========================================================
  // PERCENTAGE
  // =========================================================
  const percentage = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "0.00%";
    }

    const n = Number(value);

    if (isNaN(n)) {
      return value;
    }

    return `${n.toFixed(2)}%`;
  };

  // =========================================================
  // SEARCH REPORT
  // =========================================================
  const PrintMachineProductionReport = () => {
    if (!startDate) {
      Swal.fire(
        "Error",
        "Please Select Start Date",
        "error"
      );
      return;
    }

    if (!endDate) {
      Swal.fire(
        "Error",
        "Please Select End Date",
        "error"
      );
      return;
    }

    setIsLoading(true);
    setShowTable(false);

    setSummary([]);
    setDetails([]);

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    api
      .get(
        `Report/PrintGetMachineryProductionReport?START_DATE=${formattedStartDate}&END_DATE=${formattedEndDate}`
      )
      .then((result) => {
        console.log("API RESPONSE:", result.data);

        const apiData = result.data?.data || {};

        const summaryData =
          apiData.summary ||
          apiData.Summary ||
          [];

        const detailData =
          apiData.detail ||
          apiData.Detail ||
          [];

        console.log("SUMMARY:", summaryData);
        console.log("DETAIL:", detailData);

        setSummary(summaryData);
        setDetails(detailData);

        // IMPORTANT:
        // summaryData is the actual array.
        setShowTable(
          summaryData.length > 0 ||
          detailData.length > 0
        );

        if (
          summaryData.length === 0 &&
          detailData.length === 0
        ) {
          Swal.fire(
            "Information",
            "No data found for selected date range.",
            "info"
          );
        }
      })
      .catch((error) => {
        console.error(
          "MACHINERY REPORT ERROR:",
          error
        );

        setSummary([]);
        setDetails([]);
        setShowTable(false);

        Swal.fire(
          "Error",
          "Unable to get Machinery Production Report",
          "error"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // =========================================================
  // TOTAL
  // =========================================================
  const total = (fieldNames) => {
    return summary.reduce((sum, row) => {
      const value = Number(
        getValue(row, fieldNames, 0)
      );

      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  };

  // =========================================================
  // AVERAGE
  // =========================================================
  const average = (fieldNames) => {
    if (summary.length === 0) {
      return 0;
    }

    return (
      summary.reduce((sum, row) => {
        const value = Number(
          getValue(row, fieldNames, 0)
        );

        return sum + (isNaN(value) ? 0 : value);
      }, 0) / summary.length
    );
  };

  // =========================================================
  // GRAND TOTAL
  // =========================================================
  const grandTotal = {
  qty: total([
    "qty",
    "Qty",
    "quantity",
    "Quantity",
    "sumOfQty",
    "sumQty",
  ]),

  broken: average([
    "averageOfBroken",
    "avgBroken",
    "brokenPercentage",
  ]),

  machineHours: total([
    "sumOfMachineHours",
    "sumMachineHours",
    "machineHours",
  ]),

  perHrHTGrow: average([
    "averageOfPerHrHTGrow",
    "avgPerHrHTGrow",
    "perHrHTGrowth",
    "heightPerHour",
  ]),

  perHrWTGrow: average([
    "averageOfPerHrWTGrow",
    "avgPerHrWTGrow",
    "perHrWTGrowth",
    "weightPerHour",
  ]),

  growRate: average([
    "averageOfGrowRate",
    "avgGrowRate",
    "growRate",
  ]),

  breakage: average([
    "averageOfBreakage",
    "avgBreakage",
    "breakage",
    "brokenPercentage",
  ]),

  seedWeight: total([
    "sumOfSeedWeight",
    "sumSeedWeight",
    "seedWeight",
    "weightBefore",
  ]),

  growWeight: total([
    "sumOfGrowWeight",
    "sumGrowWeight",
    "growWeight",
    "weightAfter",
  ]),

  growthWeight: total([
    "sumOfGrow",
    "sumGrowWT",
    "growthWeight",
  ]),

  finalWeight: total([
    "sumOfFinalWeight",
    "sumFinalWeight",
    "finalWeight",
  ]),

  seed: total([
    "sumOfSeed",
    "sumSeed",
    "seed",
  ]),

  seedAndSalable: total([
    "sumOfSeedAndSalable",
    "sumSeedAndSalable",
    "seedAndSalable",
  ]),

  netSalable: total([
    "sumOfNetSalable",
    "sumNetSalable",
    "netSalable",
  ]),
};

  // =========================================================
  // EXPORT EXCEL
  // =========================================================
  const exportToExcel = () => {
    if (summary.length === 0) {
      Swal.fire(
        "Information",
        "No data to export",
        "info"
      );
      return;
    }

    const excelData = summary.map((row) => ({
      "Machine No": getValue(row, [
        "machineNo",
        "machine",
      ], ""),

      "Years": getValue(row, [
        "years",
        "year",
      ], ""),

      "Quarters": getValue(row, [
        "quarters",
        "quarter",
      ], ""),

      "Months": getValue(row, [
        "months",
        "month",
      ], ""),

      "Sum of Machine Hours (H)": getValue(row, [
        "sumOfMachineHours",
        "machineHours",
      ]),

      "Average of Per Hr HT Grow": getValue(row, [
        "averageOfPerHrHTGrow",
        "perHrHTGrowth",
        "heightPerHour",
      ]),

      "Average of Per Hr WT Grow": getValue(row, [
        "averageOfPerHrWTGrow",
        "perHrWTGrowth",
        "weightPerHour",
      ]),

      "Average of Grow Rate": getValue(row, [
        "averageOfGrowRate",
        "growRate",
      ]),

      "Average of Breakage": getValue(row, [
        "averageOfBreakage",
        "breakage",
      ]),

      "Sum of Seed Weight (SW)": getValue(row, [
        "sumOfSeedWeight",
        "seedWeight",
      ]),

      "Sum of Grow Weight (GW)": getValue(row, [
        "sumOfGrowWeight",
        "growWeight",
      ]),

      "Sum of GROW (A-B=C)": getValue(row, [
        "sumOfGrow",
        "growthWeight",
      ]),

      "Sum of Final Weight (D)": getValue(row, [
        "sumOfFinalWeight",
        "finalWeight",
      ]),

      "Sum of Seed": getValue(row, [
        "sumOfSeed",
        "seed",
      ]),

      "Sum of Seed & Salable (D+E=F)": getValue(row, [
        "sumOfSeedAndSalable",
        "seedAndSalable",
      ]),

      "Sum of Net Salable (F-A=G)": getValue(row, [
        "sumOfNetSalable",
        "netSalable",
      ]),
    }));

    // Add Grand Total
    excelData.push({
      "Machine No": "Grand Total",

      "Years": "",
      "Quarters": "",
      "Months": "",

      "Sum of Machine Hours (H)":
        grandTotal.machineHours,

      "Average of Per Hr HT Grow":
        grandTotal.perHrHTGrow,

      "Average of Per Hr WT Grow":
        grandTotal.perHrWTGrow,

      "Average of Grow Rate":
        grandTotal.growRate,

      "Average of Breakage":
        grandTotal.breakage,

      "Sum of Seed Weight (SW)":
        grandTotal.seedWeight,

      "Sum of Grow Weight (GW)":
        grandTotal.growWeight,

      "Sum of GROW (A-B-C)":
        grandTotal.growthWeight,

      "Sum of Final Weight (D)":
        grandTotal.finalWeight,

      "Sum of Seed":
        grandTotal.seed,

      "Sum of Seed & Salable (D+E-F)":
        grandTotal.seedAndSalable,

      "Sum of Net Salable (F=A-G)":
        grandTotal.netSalable,
    });

    const worksheet =
      XLSX.utils.json_to_sheet(excelData);

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Monthly Production"
    );

    XLSX.writeFile(
      workbook,
      "Monthly_Machinery_Production_Report.xlsx"
    );
  };

  // =========================================================
// GET NUMERIC VALUE
// =========================================================
const getNumber = (row, fields) => {
  const value = getValue(row, fields, 0);
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// =========================================================
// GROUP DATA
// Machine -> Year -> Quarter -> Month
// =========================================================
const getGroupedData = () => {
  const grouped = {};

  summary.forEach((row) => {
    const machine = String(
      getValue(row, ["machineNo", "machine"], "Unknown")
    );

    const year = String(
      getValue(row, ["years", "year"], "")
    );

    const quarter = String(
      getValue(row, ["quarters", "quarter"], "")
    );

    const month = String(
      getValue(row, ["months", "month"], "")
    );

    if (!grouped[machine]) {
      grouped[machine] = {};
    }

    if (!grouped[machine][year]) {
      grouped[machine][year] = {};
    }

    if (!grouped[machine][year][quarter]) {
      grouped[machine][year][quarter] = {};
    }

    if (!grouped[machine][year][quarter][month]) {
      grouped[machine][year][quarter][month] = [];
    }

    grouped[machine][year][quarter][month].push(row);
  });

  return grouped;
};

// =========================================================
// CALCULATE SUBTOTAL
// =========================================================
const calculateSubtotal = (rows) => {
  if (!rows || rows.length === 0) {
    return {
      machineHours: 0,
      perHrHTGrow: 0,
      perHrWTGrow: 0,
      growRate: 0,
      breakage: 0,
      seedWeight: 0,
      growWeight: 0,
      growthWeight: 0,
      finalWeight: 0,
      seed: 0,
      seedAndSalable: 0,
      netSalable: 0,
    };
  }

  const sum = (fields) =>
    rows.reduce(
      (total, row) =>
        total + getNumber(row, fields),
      0
    );

  const avg = (fields) =>
    rows.reduce(
      (total, row) =>
        total + getNumber(row, fields),
      0
    ) / rows.length;

  return {
   

    // =========================================
    // MACHINE HOURS
    // =========================================
    machineHours: sum([
      "sumOfMachineHours",
      "sumMachineHours",
      "machineHours",
    ]),

    // =========================================
    // HT GROW
    // =========================================
    perHrHTGrow: avg([
      "averageOfPerHrHTGrow",
      "avgPerHrHTGrow",
      "perHrHTGrowth",
      "heightPerHour",
    ]),

    // =========================================
    // WT GROW
    // =========================================
    perHrWTGrow: avg([
      "averageOfPerHrWTGrow",
      "avgPerHrWTGrow",
      "perHrWTGrowth",
      "weightPerHour",
    ]),

    // =========================================
    // GROW RATE
    // =========================================
    growRate: avg([
      "averageOfGrowRate",
      "avgGrowRate",
      "growRate",
    ]),

    // =========================================
    // BREAKAGE
    // =========================================
    breakage: avg([
      "averageOfBreakage",
      "avgBreakage",
      "breakage",
      "brokenPercentage",
    ]),

    // =========================================
    // SEED WEIGHT
    // =========================================
    seedWeight: sum([
      "sumOfSeedWeight",
      "sumSeedWeight",
      "seedWeight",
      "weightBefore",
    ]),

    // =========================================
    // GROW WEIGHT
    // =========================================
    growWeight: sum([
      "sumOfGrowWeight",
      "sumGrowWeight",
      "growWeight",
      "weightAfter",
    ]),

    // =========================================
    // GROW
    // =========================================
    growthWeight: sum([
      "sumOfGrow",
      "sumGrowWT",
      "growthWeight",
    ]),

    // =========================================
    // FINAL WEIGHT
    // =========================================
    finalWeight: sum([
      "sumOfFinalWeight",
      "sumFinalWeight",
      "finalWeight",
    ]),

    // =========================================
    // SEED
    // =========================================
    seed: sum([
      "sumOfSeed",
      "sumSeed",
      "seed",
    ]),

    // =========================================
    // SEED + SALABLE
    // =========================================
    seedAndSalable: sum([
      "sumOfSeedAndSalable",
      "sumSeedAndSalable",
      "seedAndSalable",
    ]),

    // =========================================
    // NET SALABLE
    // =========================================
    netSalable: sum([
      "sumOfNetSalable",
      "sumNetSalable",
      "netSalable",
    ]),
  };
};
  // =========================================================
  // REPORT ROW
  // =========================================================


const renderSubtotalRow = (title, rows, level = 0) => {
  const data = calculateSubtotal(rows);

  return (
    <tr
      className={`subtotal-row subtotal-level-${level}`}
    >

      {/* 1. MACHINE NO */}
      <td></td>

      {/* 2. YEARS */}
      <td></td>

      {/* 3. QUARTERS */}
      <td></td>

      {/* 4. MONTH / TOTAL TITLE */}
      <td className="subtotal-title">
        {title}
      </td>

      {/* 5. MACHINE HOURS */}
      <td>
        {number(data.machineHours)}
      </td>

      {/* 6. HT */}
      <td>
        {number(data.perHrHTGrow, 3)}
      </td>

      {/* 7. WT */}
      <td>
        {number(data.perHrWTGrow, 2)}
      </td>

      {/* 8. GROW RATE */}
      <td>
        {number(data.growRate, 0)}
      </td>

      {/* 9. BREAKAGE */}
      <td>
        {percentage(data.breakage)}
      </td>

      {/* 10. SEED WEIGHT */}
      <td>
        {number(data.seedWeight)}
      </td>

      {/* 11. GROW WEIGHT */}
      <td>
        {number(data.growWeight)}
      </td>

      {/* 12. GROW */}
      <td>
        {number(data.growthWeight)}
      </td>

      {/* 13. FINAL WEIGHT */}
      <td>
        {number(data.finalWeight)}
      </td>

      {/* 14. SEED */}
      <td>
        {number(data.seed)}
      </td>

      {/* 15. SEED + SALABLE */}
      <td>
        {number(data.seedAndSalable)}
      </td>

      {/* 16. NET SALABLE */}
      <td>
        {number(data.netSalable)}
      </td>

    </tr>
  );
};



//   const rows = [];

//   Object.keys(grouped).forEach((machine) => {

//     const machineData = grouped[machine];

//     Object.keys(machineData).forEach((year) => {

//       const yearData = machineData[year];

//       Object.keys(yearData).forEach((quarter) => {

//         const quarterData = yearData[quarter];

//         Object.keys(quarterData).forEach((month) => {

//           const monthRows =
//             quarterData[month];

//           // =============================================
//           // DETAIL ROWS
//           // =============================================

//           monthRows.forEach((row, index) => {
//             rows.push(
//               renderReportRow(
//                 row,
//                 `${machine}-${year}-${quarter}-${month}-${index}`
//               )
//             );
//           });

//           // =============================================
//           // MONTH TOTAL
//           // =============================================

//           rows.push(
//             renderSubtotalRow(
//               `${machine} - ${year} - ${quarter} - ${month} Total`,
//               monthRows,
//               3
//             )
//           );
//         });

//         // =============================================
//         // QUARTER TOTAL
//         // =============================================

//         const quarterRows = Object.values(
//           quarterData
//         ).flat();

//         rows.push(
//           renderSubtotalRow(
//             `${machine} - ${year} - ${quarter} Total`,
//             quarterRows,
//             2
//           )
//         );
//       });

//       // =============================================
//       // YEAR TOTAL
//       // =============================================

//       const yearRows = Object.values(
//         yearData
//       )
//         .flatMap((quarter) =>
//           Object.values(quarter)
//         )
//         .flat();

//       rows.push(
//         renderSubtotalRow(
//           `${machine} - ${year} Total`,
//           yearRows,
//           1
//         )
//       );
//     });

//     // =============================================
//     // MACHINE TOTAL
//     // =============================================

//     const machineRows = Object.values(
//       machineData
//     )
//       .flatMap((year) =>
//         Object.values(year)
//       )
//       .flatMap((quarter) =>
//         Object.values(quarter)
//       )
//       .flat();

//     rows.push(
//       renderSubtotalRow(
//         `${machine} Total`,
//         machineRows,
//         0
//       )
//     );
//   });

//   return rows;
// };

  // =========================================================
  // GRAND TOTAL ROW
  // =========================================================
  
 const renderHierarchicalReport = () => {
  const grouped = getGroupedData();

  const rows = [];

  Object.keys(grouped).forEach((machine) => {
    const machineData = grouped[machine];

    // Total detail rows for machine
    const machineRowCount = Object.values(machineData)
      .flatMap((year) =>
        Object.values(year)
      )
      .flatMap((quarter) =>
        Object.values(quarter)
      )
      .reduce(
        (total, monthRows) =>
          total + monthRows.length,
        0
      );

    let machineFirstRow = true;

    Object.keys(machineData).forEach((year) => {
      const yearData = machineData[year];

      // Total detail rows for year
      const yearRowCount = Object.values(yearData)
        .flatMap((quarter) =>
          Object.values(quarter)
        )
        .reduce(
          (total, monthRows) =>
            total + monthRows.length,
          0
        );

      let yearFirstRow = true;

      Object.keys(yearData).forEach((quarter) => {
        const quarterData = yearData[quarter];

        // Total detail rows for quarter
        const quarterRowCount = Object.values(
          quarterData
        ).reduce(
          (total, monthRows) =>
            total + monthRows.length,
          0
        );

        let quarterFirstRow = true;

        Object.keys(quarterData).forEach((month) => {
          const monthRows = quarterData[month];

          monthRows.forEach((row, index) => {
            rows.push(
              <tr
                key={`${machine}-${year}-${quarter}-${month}-${index}`}
                className="detail-row"
              >


  {/* 1 */}
  <td>
    {machineFirstRow ? machine : ""}
  </td>

  {/* 2 */}
  <td>
    {yearFirstRow ? year : ""}
  </td>

  {/* 3 */}
  <td>
    {quarterFirstRow ? quarter : ""}
  </td>

  {/* 4 */}
  <td>
    {index === 0 ? month : ""}
  </td>

  {/* 5 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfMachineHours",
        "sumMachineHours",
        "machineHours",
      ])
    )}
  </td>

  {/* 6 */}
  <td>
    {number(
      getNumber(row, [
        "averageOfPerHrHTGrow",
        "avgPerHrHTGrow",
        "perHrHTGrowth",
        "heightPerHour",
      ]),
      3
    )}
  </td>

  {/* 7 */}
  <td>
    {number(
      getNumber(row, [
        "averageOfPerHrWTGrow",
        "avgPerHrWTGrow",
        "perHrWTGrowth",
        "weightPerHour",
      ]),
      2
    )}
  </td>

  {/* 8 */}
  <td>
    {number(
      getNumber(row, [
        "averageOfGrowRate",
        "avgGrowRate",
        "growRate",
      ]),
      0
    )}
  </td>

  {/* 9 */}
  <td>
    {percentage(
      getNumber(row, [
        "averageOfBreakage",
        "avgBreakage",
        "breakage",
        "brokenPercentage",
      ])
    )}
  </td>

  {/* 10 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfSeedWeight",
        "sumSeedWeight",
        "seedWeight",
        "weightBefore",
      ])
    )}
  </td>

  {/* 11 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfGrowWeight",
        "sumGrowWeight",
        "growWeight",
        "weightAfter",
      ])
    )}
  </td>

  {/* 12 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfGrow",
        "sumGrowWT",
        "growthWeight",
      ])
    )}
  </td>

  {/* 13 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfFinalWeight",
        "sumFinalWeight",
        "finalWeight",
      ])
    )}
  </td>

  {/* 14 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfSeed",
        "sumSeed",
        "seed",
      ])
    )}
  </td>

  {/* 15 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfSeedAndSalable",
        "sumSeedAndSalable",
        "seedAndSalable",
      ])
    )}
  </td>

  {/* 16 */}
  <td>
    {number(
      getNumber(row, [
        "sumOfNetSalable",
        "sumNetSalable",
        "netSalable",
      ])
    )}
  </td>

</tr>

             
            );

            machineFirstRow = false;
            yearFirstRow = false;
            quarterFirstRow = false;
          });

          // MONTH TOTAL
          rows.push(
            renderSubtotalRow(
              `${month} Total`,
              monthRows,
              3
            )
          );
        });

        // QUARTER TOTAL
        const quarterRows = Object.values(
          quarterData
        ).flat();

        rows.push(
          renderSubtotalRow(
            `${quarter} Total`,
            quarterRows,
            2
          )
        );
      });

      // YEAR TOTAL
      const yearRows = Object.values(yearData)
        .flatMap((quarter) =>
          Object.values(quarter)
        )
        .flat();

      rows.push(
        renderSubtotalRow(
          `${year} Total`,
          yearRows,
          1
        )
      );
    });

    // MACHINE TOTAL
    const machineRows = Object.values(machineData)
      .flatMap((year) =>
        Object.values(year)
      )
      .flatMap((quarter) =>
        Object.values(quarter)
      )
      .flat();

    rows.push(
      renderSubtotalRow(
        `${machine} Total`,
        machineRows,
        0
      )
    );
  });

  return rows;
};
  
  
  
 const renderGrandTotal = () => {
  return (
    <tr className="grand-total">

      {/* MACHINE */}
      <td>
        Grand Total
      </td>

      {/* YEAR */}
      <td></td>

      {/* QUARTER */}
      <td></td>

      {/* MONTH */}
      <td></td>

    

      {/* MACHINE HOURS */}
      <td>
        {number(
          grandTotal.machineHours
        )}
      </td>

      {/* HT GROW */}
      <td>
        {number(
          grandTotal.perHrHTGrow,
          3
        )}
      </td>

      {/* WT GROW */}
      <td>
        {number(
          grandTotal.perHrWTGrow,
          2
        )}
      </td>

      {/* GROW RATE */}
      <td>
        {number(
          grandTotal.growRate,
          0
        )}
      </td>

      {/* BREAKAGE */}
      <td>
        {percentage(
          grandTotal.breakage
        )}
      </td>

      {/* SEED WEIGHT */}
      <td>
        {number(
          grandTotal.seedWeight
        )}
      </td>

      {/* GROW WEIGHT */}
      <td>
        {number(
          grandTotal.growWeight
        )}
      </td>

      {/* GROW */}
      <td>
        {number(
          grandTotal.growthWeight
        )}
      </td>

      {/* FINAL WEIGHT */}
      <td>
        {number(
          grandTotal.finalWeight
        )}
      </td>

      {/* SEED */}
      <td>
        {number(
          grandTotal.seed
        )}
      </td>

      {/* SEED + SALABLE */}
      <td>
        {number(
          grandTotal.seedAndSalable
        )}
      </td>

      {/* NET SALABLE */}
      <td>
        {number(
          grandTotal.netSalable
        )}
      </td>

    </tr>
  );
};

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      <ToastContainer />

      {/* =====================================================
          SEARCH SECTION
      ====================================================== */}

      <div className="card">

        <div className="card-header">
          <span>
             Production Forecast Report
          </span>
        </div>

        <div className="card-body">

          <div className="row mb-3">

            {/* START DATE */}
            <div className="col-md-3">
              <strong>
                Start Date:
              </strong>

              <input
                ref={StartDateRef}
                type="datetime-local"
                className="form-control"
                value={startDate}
                onChange={(e) =>
                  setStartDate(
                    e.target.value
                  )
                }
              />
            </div>

            {/* END DATE */}
            <div className="col-md-3">
              <strong>
                End Date:
              </strong>

              <input
                type="datetime-local"
                className="form-control"
                value={endDate}
                onChange={(e) =>
                  setEndDate(
                    e.target.value
                  )
                }
              />
            </div>

            {/* BUTTONS */}
            <div className="col-md-3 d-flex align-items-end">

              <button
                type="button"
                className="btn btn-primary"
                onClick={
                  PrintMachineProductionReport
                }
              >
                <i className="fa fa-search"></i>
                {"  "}
                Search
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={ClearData}
                style={{
                  marginLeft: "10px",
                }}
              >
                <i className="fa fa-refresh"></i>
                {"  "}
                Clear
              </button>

            </div>

          </div>

        </div>

      </div>


      {/* =====================================================
          REPORT SECTION
      ====================================================== */}

      <div className="card">

        {/* LOADER */}
        {isLoading && (
          <>
            <div className="overlay"></div>

            <div className="overlay-content text-center">

              <Spinner
                animation=""
                variant="primary"
              >
                <span className="loader"></span>
              </Spinner>

            </div>
          </>
        )}

        {/* REPORT HEADER */}
        <div className="card-header report-header">

           <div>
            <h5 className="mb-1">
            <strong>SPLENDID DIAMOND TECH PVT LTD</strong>
            </h5>

            <div>
            <strong>MACHINERY PRODUCTION REPORT</strong>
            </div>

            <div className="report-date">
             F.Y -  <strong>{formatDate(startDate)}</strong>
            &nbsp;&nbsp;&nbsp;
                   <strong>{formatDate(endDate)}</strong>
            </div>
        </div>

        </div>


        <div className="card-body">

          {/* BUTTONS */}
          {showTable && (
            <div
              className="report-buttons"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "10px",
              }}
            >

              <button
                type="button"
                className="btn btn-success"
                onClick={
                  exportToExcel
                }
              >
                <i className="fa fa-file-excel"></i>
                {"  "}
                Export Excel
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() =>
                  window.print()
                }
                style={{
                  marginLeft: "10px",
                }}
              >
                <i className="fa fa-print"></i>
                {"  "}
                Print
              </button>

            </div>
          )}


          {/* NO DATA */}
          {!showTable &&
            !isLoading && (
              <div className="text-center">

                <strong>
                  Data Not Found
                </strong>

              </div>
            )}


          {/* =================================================
              MONTHLY REPORT TABLE
          ================================================== */}

          {showTable &&
            summary.length > 0 && (

              <div className="table-responsive">

                <table className="monthly-report-table">

                  {/* ================================
                      HEADER
                  ================================= */}

                  <thead>

                    <tr>

                      <th rowSpan="2">
                        Machine No
                      </th>

                      <th rowSpan="2">
                        Years
                      </th>

                      <th rowSpan="2">
                        Quarters
                      </th>

                      <th rowSpan="2">
                        Months
                      </th>

                      <th rowSpan="2">
                        Sum of
                        <br />
                        Machine
                        <br />
                        Hours (H)
                      </th>

                      <th rowSpan="2">
                        Average of
                        <br />
                        Per Hr HT
                        <br />
                        Grow
                      </th>

                      <th rowSpan="2">
                        Average of
                        <br />
                        Per Hr WT
                        <br />
                        Grow
                      </th>

                      <th rowSpan="2">
                        Average of
                        <br />
                        Grow Rate
                      </th>

                      <th rowSpan="2">
                        Average of
                        <br />
                        Breakage
                      </th>

                      <th rowSpan="2">
                        Sum of Seed
                        <br />
                        Weight
                        <br />
                        (SW)
                      </th>

                      <th rowSpan="2">
                        Sum of Grow
                        <br />
                        Weight
                        <br />
                        (GW)
                      </th>

                      <th rowSpan="2">
                        Sum of GROW
                        <br />
                        (A-B-C)
                      </th>

                      <th rowSpan="2">
                        Sum of Final
                        <br />
                        Weight
                        <br />
                        (D)
                      </th>

                      <th rowSpan="2">
                        Sum of
                        <br />
                        Seed
                      </th>

                      <th rowSpan="2">
                        Sum of Seed
                        <br />
                        & Salable
                        <br />
                        (D+E-F)
                      </th>

                      <th rowSpan="2">
                        Sum of Net
                        <br />
                        Salable
                        <br />
                        (F=A-G)
                      </th>

                    </tr>

                  </thead>


                  {/* ================================
                      BODY
                  ================================= */}

                  <tbody>

                     {renderHierarchicalReport()}

  

                    {/* GRAND TOTAL */}

                    {renderGrandTotal()}

                  </tbody>

                </table>

              </div>

            )}

        </div>

      </div>

    </>
  );
};

export default GetMachineryProductionReport;